import { useState, useEffect } from "react";
import { addDays, parse } from "date-fns";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { 
  Loader2, 
  Sparkles, 
  Send, 
  Trash2, 
  RefreshCw,
  MessageSquare,
  X,
  ChevronLeft,
  Plane as PlaneIcon,
  Building as BuildingIcon,
  MapPin as MapPinIcon,
  Utensils as UtensilsIcon,
  Car as CarIcon,
  Circle as CircleIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { TripComponent, ComponentType, Trip } from "@shared/schema";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";

interface AIPlannerSheetProps {
  tripId: number;
  dateRange: [Date, Date];
  onGenerateSuccess: () => void;
  trigger?: React.ReactNode;
  tripName?: string;
  destinationDetails?: string;
  autoGenerate?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function AIPlannerSheet({
  tripId,
  dateRange,
  onGenerateSuccess,
  trigger,
  tripName,
  destinationDetails,
  autoGenerate = false,
  open: controlledOpen,
  onOpenChange
}: AIPlannerSheetProps) {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled or uncontrolled open state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  
  // Handler for open state changes
  const handleOpenChange = (newOpenState: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpenState);
    } else {
      setInternalOpen(newOpenState);
    }
  };
  
  // Handler to close the sheet
  const closeSheet = () => {
    handleOpenChange(false);
  };
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedComponents, setGeneratedComponents] = useState<TripComponent[]>([]);
  const [activeTab, setActiveTab] = useState("input");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<{role: "user" | "assistant", content: string}[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [editComponentIndex, setEditComponentIndex] = useState<number | null>(null);
  const [editPrompt, setEditPrompt] = useState("");
  const { toast } = useToast();
  
  // Get full trip details for metadata
  const { data: trip } = useQuery<Trip>({
    queryKey: [`/api/trips/${tripId}`],
    enabled: !!tripId,
  });
  
  // Generate a default prompt based on trip details
  useEffect(() => {
    if (open && !prompt && (tripName || destinationDetails || trip)) {
      const startDate = dateRange[0].toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const endDate = dateRange[1].toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const duration = Math.floor((dateRange[1].getTime() - dateRange[0].getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      let generatedPrompt = `Please create a detailed day-by-day itinerary for my trip`;
      
      if (tripName) {
        generatedPrompt += ` to ${tripName}`;
      }
      
      // Add countries information if available
      if (trip?.countries && (trip.countries as string[]).length > 0) {
        const countryList = (trip.countries as string[]).join(", ");
        generatedPrompt += ` visiting ${countryList}`;
      }
      
      generatedPrompt += ` from ${startDate} to ${endDate} (${duration} days).`;
      
      if (destinationDetails) {
        generatedPrompt += ` The trip is to ${destinationDetails}.`;
      }
      
      // Add client information if available
      if (trip?.clientName) {
        generatedPrompt += ` This trip is for client ${trip.clientName}.`;
      }
      
      if (trip?.clientInterests) {
        generatedPrompt += ` The client is interested in ${trip.clientInterests}.`;
      }
      
      // Add assumption about departing from the US
      generatedPrompt += ` Assume I'm departing from the United States, so please include appropriate international flights.`;
      
      generatedPrompt += ` Please include a good mix of activities, food, transportation and accommodation options appropriate for this destination. I'd like a balanced itinerary with popular attractions and some local experiences.`;
      
      // Add instruction about avoiding either-or options
      generatedPrompt += ` Important: Do not create components with multiple options or alternatives within a single item (like "Option 1: X OR Option 2: Y"). Each component should be a single, clear recommendation, not a choice between options.`;
      
      // Add instruction about organizing by day
      generatedPrompt += ` Please organize the itinerary clearly by day, with appropriate morning, afternoon, and evening activities for each day.`;
      
      setPrompt(generatedPrompt);
      
      // If autoGenerate is true, automatically generate itinerary
      if (autoGenerate) {
        setTimeout(() => {
          handleGenerate();
        }, 500);
      }
    }
  }, [open, tripName, destinationDetails, dateRange, autoGenerate, trip]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a description of your trip",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/trips/${tripId}/generate-itinerary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate itinerary");
      }

      const data = await response.json();
      setGeneratedComponents(data.components);
      setActiveTab("preview");
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate itinerary",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle chat with AI to refine itinerary
  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatInput("");
    setChatLoading(true);
    
    // Add user message to chat
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    
    try {
      // Send the entire itinerary and the chat message to AI for refinement
      const response = await fetch(`/api/trips/${tripId}/refine-itinerary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          components: generatedComponents,
          userMessage,
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to refine itinerary");
      }
      
      const data = await response.json();
      
      // Add AI response to chat
      setChatMessages(prev => [...prev, { role: "assistant", content: data.message }]);
      
      // Update components if the AI provided new ones
      if (data.components && data.components.length > 0) {
        setGeneratedComponents(data.components);
      }
    } catch (error) {
      console.error("Chat refinement error:", error);
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I encountered an error while trying to refine your itinerary. Please try again." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };
  
  // Delete a component
  const handleDeleteComponent = (index: number) => {
    setGeneratedComponents(prev => prev.filter((_, i) => i !== index));
    toast({
      title: "Component Removed",
      description: "The item has been removed from your itinerary",
    });
  };
  
  // Open dialog to replace a component
  const handleReplaceComponent = (index: number) => {
    setEditComponentIndex(index);
    const component = generatedComponents[index];
    setEditPrompt("");
  };
  
  // Submit request to replace a component
  const handleSubmitReplace = async () => {
    if (editComponentIndex === null) return;
    
    const componentToReplace = generatedComponents[editComponentIndex];
    setChatLoading(true);
    
    try {
      const response = await fetch(`/api/trips/${tripId}/replace-component`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          component: componentToReplace,
          prompt: editPrompt,
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to replace component");
      }
      
      const data = await response.json();
      
      // Replace the component with the new one
      setGeneratedComponents(prev => {
        const newComponents = [...prev];
        newComponents[editComponentIndex] = data.component;
        return newComponents;
      });
      
      setEditComponentIndex(null);
      setEditPrompt("");
      
      toast({
        title: "Component Replaced",
        description: "The item has been replaced in your itinerary",
      });
    } catch (error) {
      console.error("Component replacement error:", error);
      toast({
        title: "Error",
        description: "Failed to replace component",
        variant: "destructive",
      });
    } finally {
      setChatLoading(false);
    }
  };
  
  // Save the itinerary to the trip
  const handleSave = async () => {
    if (generatedComponents.length === 0) {
      toast({
        title: "Error",
        description: "No components generated yet",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Track failed and successful components
      let successCount = 0;
      let failedCount = 0;
      
      // Process components sequentially to avoid overwhelming the server
      for (const component of generatedComponents) {
        try {
          // Ensure component has correct structure before sending
          const sanitizedComponent = {
            tripId: component.tripId,
            type: component.type,
            title: component.title || "Untitled Activity",
            description: component.description || null,
            dayIndex: component.dayIndex || 0,
            startDate: component.startDate,
            endDate: component.endDate || null,
            location: component.location || null,
            startTime: component.startTime || null,
            endTime: component.endTime || null,
            details: component.details || {}
          };
          
          const response = await fetch("/api/components", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(sanitizedComponent),
          });

          if (response.ok) {
            successCount++;
          } else {
            failedCount++;
            console.error(`Failed to save component: ${component.title}`, 
              await response.json());
          }
        } catch (err) {
          failedCount++;
          console.error(`Error processing component: ${component.title}`, err);
        }
      }

      // Day destinations are now directly created by the AI on the server side
      
      if (successCount > 0) {
        // Close the sheet and trigger success callback first
        closeSheet();
        
        // Slight delay to ensure UI updates properly
        setTimeout(() => {
          // Call the callback to trigger data refresh
          onGenerateSuccess();
          
          // Show success toast
          toast({
            title: "Success",
            description: `Added ${successCount} items to your itinerary${
              failedCount > 0 ? ` (${failedCount} items failed)` : ""
            }`,
          });
        }, 100);
      } else {
        toast({
          title: "Error",
          description: "Failed to save any components to your itinerary",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error saving itinerary:", error);
      toast({
        title: "Error",
        description: "Failed to save generated itinerary",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (generatedComponents.length > 0) {
      const confirmed = window.confirm("Are you sure you want to start over? All generated components will be lost.");
      if (!confirmed) return;
    }
    
    setPrompt("");
    setGeneratedComponents([]);
    setChatMessages([]);
    setActiveTab("input");
    setChatOpen(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          className={isMobile ? "h-[90vh] max-h-[90vh]" : "w-[80vw] sm:max-w-[600px] overflow-y-auto"}
        >
          <SheetHeader className="space-y-1 mb-4">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Trip Planner
            </SheetTitle>
            <p className="text-sm text-muted-foreground">
              Generate a personalized itinerary for your trip using AI
            </p>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="input">Input</TabsTrigger>
              <TabsTrigger value="preview" disabled={generatedComponents.length === 0}>
                Preview
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="input" className="space-y-4 mt-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Describe your trip and what you'd like to do. Be as specific as possible
                  about your interests, preferences, and any must-see attractions.
                </p>
                <Textarea
                  placeholder="I'm going to Paris for 3 days and want to see the main attractions, enjoy local cuisine, and visit some off-the-beaten-path spots..."
                  className="min-h-[200px]"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={loading}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                {generatedComponents.length > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Start Over
                  </Button>
                )}
                <Button 
                  onClick={handleGenerate} 
                  disabled={loading || !prompt.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Itinerary
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="preview" className="space-y-4 mt-4">
              {generatedComponents.length > 0 ? (
                <>
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Itinerary Components List */}
                    <div className="border rounded-md p-4 space-y-4 flex-1">
                      <div className="flex justify-between items-center">
                        <h3 className="font-medium">Generated Itinerary</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setChatOpen(!chatOpen)}
                          className="flex items-center gap-1"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span>{chatOpen ? "Hide Chat" : "AI Chat"}</span>
                        </Button>
                      </div>
                      <div className={`space-y-6 ${!isMobile ? "max-h-[400px]" : "max-h-[250px]"} overflow-y-auto pr-1`}>
                        {/* Group components by day and render with day headers */}
                        {(() => {
                          // Group components by day
                          const groupedByDay: Record<number, TripComponent[]> = {};
                          generatedComponents.forEach(component => {
                            const dayIndex = component.dayIndex || 0;
                            if (!groupedByDay[dayIndex]) {
                              groupedByDay[dayIndex] = [];
                            }
                            groupedByDay[dayIndex].push(component);
                          });
                          
                          // Create an array of day indices and sort them
                          const dayIndices = Object.keys(groupedByDay).map(Number).sort((a, b) => a - b);
                          
                          // Use the dateRange[0] directly (it's already been properly adjusted in useTrip.ts)
                          // for date calculations
                          const startDate = dateRange[0];
                          
                          return dayIndices.map(dayIndex => {
                            // Calculate this day's date (timezone-neutral)
                            const currentDate = addDays(startDate, dayIndex);
                            const formattedDate = currentDate.toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric'
                            });
                            
                            return (
                              <div key={dayIndex} className="space-y-3">
                                {/* Day header */}
                                <div className="sticky top-[var(--global-header-height,35px)] bg-background/95 backdrop-blur-sm z-10 border-b pb-2">
                                  <h3 className="font-semibold text-lg text-primary">
                                    Day {dayIndex + 1}: {formattedDate}
                                  </h3>
                                </div>
                                
                                {/* Day's components */}
                                <div className="space-y-3 pl-2">
                                  {groupedByDay[dayIndex].map((component, index) => {
                                    // Determine time of day based on startTime
                                    let timeOfDay = "";
                                    if (component.startTime) {
                                      const hour = parseInt(component.startTime.split(':')[0]);
                                      if (hour < 12) timeOfDay = "Morning";
                                      else if (hour < 17) timeOfDay = "Afternoon";
                                      else timeOfDay = "Evening";
                                    }
                                    
                                    // Component type icon
                                    let icon;
                                    switch (component.type) {
                                      case "flight":
                                        icon = <PlaneIcon className="h-4 w-4 text-blue-500" />;
                                        break;
                                      case "hotel":
                                        icon = <BuildingIcon className="h-4 w-4 text-orange-500" />;
                                        break;
                                      case "activity":
                                        icon = <MapPinIcon className="h-4 w-4 text-green-500" />;
                                        break;
                                      case "food":
                                        icon = <UtensilsIcon className="h-4 w-4 text-red-500" />;
                                        break;
                                      case "transport":
                                        icon = <CarIcon className="h-4 w-4 text-purple-500" />;
                                        break;
                                      default:
                                        icon = <CircleIcon className="h-4 w-4 text-gray-500" />;
                                    }
                                    
                                    return (
                                      <div key={index} className="border rounded-md p-3 relative group">
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1 pr-16">
                                            <div className="flex items-center gap-2">
                                              {icon}
                                              <p className="font-medium">{component.title}</p>
                                            </div>
                                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                              {timeOfDay && (
                                                <span className="bg-muted px-1.5 py-0.5 rounded-sm text-xs">
                                                  {timeOfDay}
                                                </span>
                                              )}
                                              <span>
                                                {component.type} • 
                                                {component.startTime && ` ${component.startTime}`}
                                                {component.endTime && ` - ${component.endTime}`}
                                              </span>
                                            </p>
                                            {component.location && (
                                              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                                                <MapPinIcon className="h-3 w-3" />
                                                {component.location}
                                              </p>
                                            )}
                                            {component.description && (
                                              <p className="text-sm mt-2">{component.description}</p>
                                            )}
                                          </div>
                                          <div className="absolute top-3 right-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex gap-1">
                                            <Button 
                                              variant="ghost" 
                                              size="icon" aria-label={`Replace ${component.title || "this component"}`} 
                                              onClick={() => handleReplaceComponent(generatedComponents.findIndex(c => c === component))}
                                              className="h-8 w-8"
                                            >
                                              <RefreshCw className="h-4 w-4" />
                                            </Button>
                                            <Button 
                                              variant="ghost" 
                                              size="icon" aria-label={`Delete ${component.title || "this component"}`} 
                                              onClick={() => handleDeleteComponent(generatedComponents.findIndex(c => c === component))}
                                              className="h-8 w-8 text-destructive"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    
                    {/* Chat Interface */}
                    {chatOpen && (
                      <div className="border rounded-md p-4 w-full lg:w-96 flex flex-col h-[300px] lg:h-[400px]">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-medium flex items-center gap-1">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <span>AI Assistant</span>
                          </h3>
                          <Button 
                            variant="ghost" 
                            size="icon" aria-label="Close AI chat" 
                            onClick={() => setChatOpen(false)}
                            className="h-8 w-8"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto mb-3 space-y-4 bg-muted/20 p-3 rounded-md">
                          {chatMessages.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">
                              Ask the AI assistant to help refine this itinerary
                            </p>
                          ) : (
                            chatMessages.map((msg, i) => (
                              <div 
                                key={i} 
                                className={`flex ${
                                  msg.role === "user" ? "justify-end" : "justify-start"
                                }`}
                              >
                                <div 
                                  className={`max-w-[80%] rounded-lg p-3 ${
                                    msg.role === "user" 
                                      ? "bg-primary text-primary-foreground" 
                                      : "bg-muted"
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                </div>
                              </div>
                            ))
                          )}
                          {chatLoading && (
                            <div className="flex justify-start">
                              <div className="max-w-[80%] rounded-lg p-3 bg-muted">
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Input
                            className="flex-1"
                            placeholder="Ask questions or suggest changes..."
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && !chatLoading && handleChatSend()}
                            disabled={chatLoading}
                          />
                          <Button 
                            size="icon" aria-label="Send message"
                            disabled={!chatInput.trim() || chatLoading}
                            onClick={handleChatSend}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setActiveTab("input")}>
                      Back to Edit
                    </Button>
                    <Button onClick={handleSave} disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Add to Itinerary"
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p>No itinerary generated yet. Go back to input your trip description.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveTab("input")}
                    className="mt-4"
                  >
                    Back to Input
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Component Replacement Dialog */}
      {editComponentIndex !== null && (
        <Dialog 
          open={editComponentIndex !== null} 
          onOpenChange={(open) => !open && setEditComponentIndex(null)}
        >
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-primary" />
                Replace Component
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Describe what you'd like to replace this with
              </p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {editComponentIndex !== null && (
                <div className="border rounded-md p-3 bg-muted/30">
                  <p className="font-medium">{generatedComponents[editComponentIndex].title}</p>
                  <p className="text-sm text-muted-foreground">
                    Day {generatedComponents[editComponentIndex].dayIndex + 1} • {generatedComponents[editComponentIndex].type}
                  </p>
                  {generatedComponents[editComponentIndex].description && (
                    <p className="text-sm mt-2">{generatedComponents[editComponentIndex].description}</p>
                  )}
                </div>
              )}
              <Textarea
                placeholder="I'd like something less touristy, focusing more on local culture and food..."
                className="min-h-[100px]"
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                disabled={chatLoading}
              />
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEditComponentIndex(null)}
                disabled={chatLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitReplace}
                disabled={chatLoading || !editPrompt.trim()}
              >
                {chatLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Replace Component"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
